import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNoticeStore } from '../stores/notice.store.js';
import { feedbackSound } from '../services/audio/feedback.js';
import { haptics } from '../services/telegram.js';
import { useT } from '../i18n/index.js';

/**
 * The notice slab, in the middle of the app.
 *
 * It is one painted panel with the words laid into the space the master left
 * for them. Tapping anywhere dismisses it; so does Escape, and so does time —
 * a success clears itself, a refusal waits, because being told why something
 * failed is worth reading twice.
 */
const AUTO_DISMISS_MS = { success: 4200, error: 0 } as const;

/** How big the headline can be before it has to give way to the slab. */
function titleSize(title: string): 'sm' | 'md' | 'lg' {
  const length = title.trim().length;
  if (length <= 10) return 'lg';
  if (length <= 20) return 'md';
  return 'sm';
}

/** Below this the words stop reading as part of the slab. */
const MIN_FIT = 0.62;

export function Notice() {
  const { t, maybe } = useT();
  /** A catalogue key (the helpers' fixed words) in the reader's language; anything else as it came. */
  const say = (text: string) => maybe(text) ?? text;
  const current = useNoticeStore((state) => state.current);
  const dismiss = useNoticeStore((state) => state.dismiss);
  const [leaving, setLeaving] = useState(false);
  const timer = useRef<number | null>(null);
  const stack = useRef<HTMLSpanElement>(null);

  const close = () => {
    if (leaving) return;
    setLeaving(true);
    window.setTimeout(() => {
      setLeaving(false);
      dismiss();
    }, 180);
  };

  useEffect(() => {
    if (!current) return;
    haptics.notify(current.kind === 'success' ? 'success' : 'error');
    // Every notice is heard: a reward or a success with the wheel's USDT win,
    // an error with its "nothing this time" (services/audio/feedback.ts).
    if (current.kind === 'success') feedbackSound.success();
    else feedbackSound.error();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') close();
    };
    document.addEventListener('keydown', onKey);

    const wait = AUTO_DISMISS_MS[current.kind];
    if (wait > 0) timer.current = window.setTimeout(close, wait);

    return () => {
      document.removeEventListener('keydown', onKey);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  /*
   * The headline and its line share the room the stylesheet gives the column.
   * Wrapped words can need more than that: then both are set smaller together,
   * a step at a time, until they fit (never below MIN_FIT). Measured from the
   * first word's top to the last one's bottom, since an overflowing column
   * spills both ways; and again once the web fonts are in, which change it.
   */
  useLayoutEffect(() => {
    const element = stack.current;
    if (!element) return undefined;
    let alive = true;
    const fit = () => {
      const first = element.firstElementChild;
      const last = element.lastElementChild;
      if (!alive || !first || !last) return;
      const need = () => last.getBoundingClientRect().bottom - first.getBoundingClientRect().top;
      const room = element.getBoundingClientRect().height;
      let value = 1;
      element.style.setProperty('--fit', '1');
      while (value > MIN_FIT && need() > room + 0.5) {
        value = Math.max(MIN_FIT, value - 0.04);
        element.style.setProperty('--fit', value.toFixed(2));
      }
    };
    fit();
    void document.fonts?.ready.then(fit);
    return () => {
      alive = false;
    };
  }, [current?.id]);

  if (!current) return null;

  return createPortal(
    <div
      className={leaving ? 'notice-root is-leaving' : 'notice-root'}
      role="alertdialog"
      aria-modal="true"
      aria-label={`${say(current.kicker)}: ${current.title}`}
      onClick={close}
    >
      <button type="button" className={`notice notice--${current.kind}`} aria-label={t('common.dismiss')}>
        <span className="notice__at notice__kicker">{say(current.kicker)}</span>
        <span className="notice__stack" ref={stack}>
          <span className={`notice__at notice__title notice__title--${titleSize(current.title)}`}>
            {current.title}
          </span>
          {current.subtitle ? <span className="notice__at notice__subtitle">{say(current.subtitle)}</span> : null}
        </span>
      </button>
    </div>,
    document.body,
  );
}
